import { additionalDataBlock } from './additionalData';
import {
    domainIdsForAdditionalData,
    REPORT_DATA_SOURCE_PARAM,
    reportDataInfoKey,
    sourceMarkerForTurn,
    toolsForSelectedSources,
} from './sourceTools';
import {
    applyReportWhyTexts,
    collectWhyReportIds,
    collectWhyReportNames,
    requestReportWhy,
    stripReportWhy,
} from './reportWhy';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import {
    IDomain,
    setAvailableDomains,
    setSelectedDomains,
    MappedDomains,
    setAvailableTools,
    setDefaultSelectedDomains,
    ITool,
    setOnlySearch,
    clearPrependContext,
    REPORT_DATA_SOURCE_NAME,
} from 'store/conciergeSlice';
import { conciergeAPI } from 'api/concierge';
import type { ITestSession } from 'api/concierge';
import { uid } from 'tools/tools';
import {
    JSON_IN_MESSAGE_REGEX,
    JSON_START_MESSAGE_REGEX,
    THINKING_IN_MESSAGE_REGEX,
    parseJsonMessage,
    parseMessage,
} from '../tools';
import { setWaitingConcierge } from 'store/globalSearchSlice';
import useBundleTranslation from 'i18n';
import { AdditionalData, ErrorBlock, IDialogItem, IMessagePart, StreamThinking } from '../types';
import { useMatches } from 'react-router-dom';
import { RouteItemType } from 'routes';
import { collectReportContext, reportContextCaptureOn } from '../reportContextCapture';
import { isLearningUpdateCommand } from '../tools/learning-update-command';
import { offersProceed } from '../tools/auth-required';

let currentController: AbortController | null = null;

const checkLogout = (message: string) => {
    let response = {
        error: '',
        status: '',
        url: '',
        params: '',
        type: '',
    };

    try {
        response = JSON.parse(message);
    } catch (error) {
        return false;
    }

    if (!response || typeof response !== 'object') {
        return false;
    }

    if ('ERROR' == response.status && ('Unauthorized' == response.error || 'NotFound' == response.error)) {
        let url = '/auth/deny/type/' + response.type;

        if (response.params) url += '?' + new URLSearchParams(response.params);

        document.location = url;
    } else if ('ERROR' == response.status && 'need_redirect' == response.error && response.url) {
        if (document.location.href != response.url) document.location = response.url;
        else document.location.reload();
    }

    return false;
};

// Resolve caller-supplied domain names (shared-components API config) to the
// real domain.name. Exact match wins; otherwise fall back to trim +
// case-insensitive, but only when it resolves to exactly one domain — never
// guess on ambiguity. Storing canonical names keeps all downstream exact
// comparisons working unchanged.
const canonicalizeDomainNames = (names: string[], reference: { name: string }[]): string[] => {
    const result = new Set<string>();
    for (const raw of names) {
        if (reference.some((d) => d.name === raw)) {
            result.add(raw);
            continue;
        }
        const needle = raw.trim().toLowerCase();
        const matches = reference.filter((d) => d.name.trim().toLowerCase() === needle);
        if (matches.length === 1) {
            result.add(matches[0].name);
        }
    }
    return Array.from(result);
};

export interface CustomInstructions {
    mcp_post_processing?: string;
    llm_comment?: string;
}

export interface UseConciergeChartConfig {
    startNewChatAfter?: number;
    includeContentSearch?: boolean;
    includeHelpDocs?: boolean;
    includeGlossaryTerms?: boolean;
    includeCustomFaq?: boolean;
    availableDomains?: string[];
    selectedDomains?: string[];
    isAppPanel?: boolean;
    extraUrlParams?: { [key: string]: string };
    getContext?: () => any | Promise<any>;
    customInstructions?: CustomInstructions;
}

export type ChatId = number | string;

export interface UseConciergeChartResult {
    dialog: IDialogItem[];
    setDialog: React.Dispatch<React.SetStateAction<IDialogItem[]>>;
    loading: boolean;
    activeChat: ChatId;
    setActiveChat: React.Dispatch<React.SetStateAction<ChatId>>;
    activeChatRequest: string;
    onRequestSubmit: (request: string) => void;
    startNewChat: () => void;
    onAbort: () => void;
    silentAbort: () => void;
    isElementViewer: boolean;
    mappedDomains: MappedDomains;
    domainsData: IDomain[] | undefined;
    toolsData: ITool[] | undefined;
    onResendRequest: (excludedToolsFromAuth: string[]) => void;
    onRetryError: () => void;
    setLastInteraction: React.Dispatch<React.SetStateAction<number>>;
    clearActiveChatRequest: () => void;
    startNewResponseBlock: () => void;
    openChat: (conversationTs: string, dialog: IDialogItem[], whyMessageUids: Record<string, string>) => void;
}

export function useConciergeChat(config: UseConciergeChartConfig, visible: boolean): UseConciergeChartResult {
    const {
        startNewChatAfter,
        includeContentSearch,
        includeHelpDocs,
        includeGlossaryTerms,
        includeCustomFaq,
        availableDomains,
        selectedDomains,
        isAppPanel,
        extraUrlParams,
        getContext,
        customInstructions,
    } = config;

    const { t } = useBundleTranslation(['app/concierge/widget']);

    const dispatch = useDispatch();
    const store = useStore();
    const matches = useMatches();

    const onlySearch = useSelector((state: any) => state.concierge.onlySearch);
    const selectedConciergeDomains = useSelector((state: any) => state.concierge.selectedDomains) as string[];
    const reduxAvailableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;
    const testSession = useSelector((state: any) => state.concierge.testSession);
    const launchElementId = useSelector((state: any) => state.concierge.launchElementId) as number | null;
    const editorContextElementId = useSelector((state: any) => state.concierge.editorContext?.elementId ?? null) as
        number | null;

    const [activeChat, setActiveChat] = useState<ChatId>(0);
    const [activeChatRequest, setActiveChatRequest] = useState('');
    const activeChatRequestRef = useRef('');
    const [loading, setLoading] = useState(false);
    const [dialog, setDialog] = useState<IDialogItem[]>([]);
    /** The backend message uid behind each response item, for the why request. */
    const whyMessageUidRef = useRef<Record<string, string>>({});
    const whyRequestedRef = useRef<Set<string>>(new Set());
    /** Bumped when a stored chat replaces the dialog: a request from before it must not touch the new one. */
    const requestGenerationRef = useRef(0);
    const [lastInteraction, setLastInteraction] = useState(Date.now());
    const [mappedDomains, setMappedDomains] = useState<MappedDomains>({});
    const [reportDataDomain, setReportDataDomain] = useState<IDomain | null>(null);
    const [proceedMode, setProceedMode] = useState(false);
    const [excludedTools, setExcludedTools] = useState<string[]>([]);
    const [pendingNewChat, setPendingNewChat] = useState<string | null>(null);

    const { data: domainsData } = useQuery<IDomain[]>({
        queryKey: ['conciergeDomains'],
        queryFn: () => conciergeAPI.getDomains(),
        // A domain switched off in another tab must vanish from the next question without a reload.
        refetchOnWindowFocus: 'always',
        enabled: visible,
        staleTime: 5 * 60 * 1000,
    });

    const { data: toolsData } = useQuery<ITool[]>({
        queryKey: ['conciergeTools'],
        queryFn: () => conciergeAPI.getTools(),
        refetchOnWindowFocus: 'always',
        enabled: visible && !!domainsData,
        staleTime: 5 * 60 * 1000,
    });

    const isElementViewer: boolean = (() => {
        if (matches?.length) {
            return (matches[matches.length - 1].handle as RouteItemType)?.isElementViewer ?? false;
        }
        return false;
    })();

    const currentElementId: string | undefined = isElementViewer
        ? (matches[matches.length - 1].params?.elementId as string | undefined)
        : undefined;

    useEffect(() => {
        if (isElementViewer && currentElementId && mappedDomains[currentElementId] === undefined) {
            conciergeAPI.getMappedDomains(currentElementId).then((domains: string[]) => {
                setMappedDomains((prev) => ({
                    ...prev,
                    [currentElementId]: domains,
                }));
            });
        }
    }, [isElementViewer, currentElementId]);

    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const useStream = conciergeSettings.response_streaming_support_ind == 'Y';

    const responseUIDRef = useRef<string>(uid());
    const requestUIDRef = useRef<string>(uid());
    const currentPath = window.location.pathname;

    const removeProceedButtons = useCallback(() => {
        setDialog((prevDialog) => {
            const hasProceedButton = prevDialog.some((item) => {
                return item.parts.some((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        return part.jsonBlocks.some((block) => block.data_type === 'proceed-button');
                    }
                    return false;
                });
            });

            if (!hasProceedButton) {
                return prevDialog;
            }

            return prevDialog.filter((item) => {
                const hasProceedButtonInItem = item.parts.some((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        return part.jsonBlocks.some((block) => block.data_type === 'proceed-button');
                    }
                    return false;
                });
                return !hasProceedButtonInItem;
            });
        });
    }, []);

    const responseToContent = useCallback(
        (message: string) => {
            if (checkLogout(message)) {
                return;
            }

            const messageParts = parseMessage(message, activeChat.toString());

            setDialog((prevState) => {
                const newState = [...prevState];
                let messageUID = responseUIDRef.current;

                const index = newState.findIndex((item) => item.uid === messageUID);

                if (index > -1) {
                    for (const messagePart of messageParts) {
                        if (messagePart?.type === 'invalid-request') {
                            newState[index].parts.push(messagePart);
                            continue;
                        }

                        if (messagePart?.type !== 'json') {
                            const jsonStartIndex = newState[index].parts.findLastIndex(
                                (part) => part.type == 'json_start',
                            );

                            if (jsonStartIndex > -1) {
                                const newJsonPart = newState[index].parts[jsonStartIndex].message + messagePart.message;
                                const jsonMatch = newJsonPart.match(JSON_IN_MESSAGE_REGEX);
                                const thinkingMatch = !jsonMatch ? newJsonPart.match(THINKING_IN_MESSAGE_REGEX) : null;
                                const anyMatch = jsonMatch ?? thinkingMatch;

                                if (anyMatch) {
                                    const regIndex = newJsonPart.indexOf(anyMatch[0]);
                                    const beforeJsonContent = newJsonPart.substring(0, regIndex);
                                    const afterJsonContent = newJsonPart.substring(regIndex + anyMatch[0].length);

                                    // Row 2244: the text beside the block is held as the server wrote it, each side once:
                                    // what stood before a marker that was itself cut is held here, what follows the block
                                    // below. Only the blocks are taken from the parsed parts: their text is trimmed, and
                                    // the text after the block was held a second time from them.
                                    if (beforeJsonContent.trim()) {
                                        newState[index].parts.push({
                                            type: 'markdown',
                                            message: beforeJsonContent,
                                            source: 'backend',
                                        });
                                    }

                                    const parsedParts = parseJsonMessage(newJsonPart);
                                    for (const parsedPart of parsedParts) {
                                        if (parsedPart.type === 'json' && parsedPart.jsonBlocks?.length) {
                                            for (const block of parsedPart.jsonBlocks) {
                                                const existingBlockIndex =
                                                    block.data_type === 'next-source'
                                                        ? -1
                                                        : newState[index].parts.findIndex(
                                                              (part) =>
                                                                  part.type === 'json' &&
                                                                  part.jsonBlocks?.some(
                                                                      (existingBlock) =>
                                                                          JSON.stringify(existingBlock) ===
                                                                          JSON.stringify(block),
                                                                  ),
                                                          );

                                                if (existingBlockIndex > -1) {
                                                    continue;
                                                }

                                                const partWithBlock = {
                                                    ...parsedPart,
                                                    jsonBlocks: [block],
                                                };

                                                if (block.data_type === 'stream_thinking') {
                                                    const idx = newState.findIndex((item) => item.uid === messageUID);
                                                    if (idx > -1) {
                                                        const existingPartIdx = newState[idx].parts.findIndex(
                                                            (part) =>
                                                                part.type === 'json' &&
                                                                part.jsonBlocks?.some(
                                                                    (b) => b.data_type === 'stream_thinking',
                                                                ),
                                                        );
                                                        if (existingPartIdx > -1) {
                                                            const existingBlock = newState[idx].parts[existingPartIdx]
                                                                .jsonBlocks![0] as StreamThinking;
                                                            newState[idx].parts[existingPartIdx] = {
                                                                ...newState[idx].parts[existingPartIdx],
                                                                jsonBlocks: [
                                                                    {
                                                                        ...existingBlock,
                                                                        content: [
                                                                            ...existingBlock.content,
                                                                            ...(block as StreamThinking).content,
                                                                        ],
                                                                    },
                                                                ],
                                                            };
                                                        } else {
                                                            newState[idx].parts.push(partWithBlock);
                                                        }
                                                    }
                                                    continue;
                                                }

                                                if (block.data_type === 'next-block') {
                                                    const newUID = uid();
                                                    responseUIDRef.current = newUID;
                                                    messageUID = newUID;

                                                    newState.push({
                                                        uid: messageUID,
                                                        type: 'response',
                                                        parts: [partWithBlock],
                                                        parameters: {
                                                            requestPath: currentPath,
                                                        },
                                                    });
                                                } else {
                                                    const idx = newState.findIndex((item) => item.uid === messageUID);
                                                    if (idx > -1) {
                                                        newState[idx].parts.push(partWithBlock);
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    newState[index].parts.splice(jsonStartIndex, 1);
                                    if (afterJsonContent.trim()) {
                                        newState[index].parts.push({
                                            type: 'markdown',
                                            message: afterJsonContent,
                                            source: 'backend',
                                        });
                                    }
                                } else {
                                    newState[index].parts[jsonStartIndex].message = newJsonPart;
                                }
                            } else {
                                if (messagePart?.type === 'markdown') {
                                    const lastPart = newState[index].parts.pop();

                                    if (lastPart) {
                                        if (lastPart.type === 'markdown') {
                                            const resultMessage = lastPart.message + messagePart.message;

                                            const match = resultMessage.match(JSON_START_MESSAGE_REGEX);
                                            if (match) {
                                                const regIndex = resultMessage.indexOf(match[0]);

                                                const beforeMatch = resultMessage.slice(0, regIndex);
                                                if (beforeMatch) {
                                                    newState[index].parts.push({
                                                        message: beforeMatch,
                                                        type: 'markdown',
                                                        source: 'backend',
                                                    });
                                                }

                                                const afterMatch = resultMessage.slice(regIndex + match[0].length);

                                                newState[index].parts.push({
                                                    message: match[0] + afterMatch,
                                                    type: 'json_start',
                                                    source: 'backend',
                                                });
                                            } else {
                                                newState[index].parts.push({
                                                    ...lastPart,
                                                    message: resultMessage,
                                                });
                                            }
                                        } else {
                                            newState[index].parts.push(lastPart);
                                            newState[index].parts.push(messagePart);
                                        }
                                    } else {
                                        newState[index].parts.push(messagePart);
                                    }
                                } else {
                                    newState[index].parts.push(messagePart);
                                }
                            }
                            continue;
                        }

                        if (messagePart.type === 'json' && messagePart.jsonBlocks?.length) {
                            for (const block of messagePart.jsonBlocks) {
                                if (block.data_type === 'stream_thinking') {
                                    const idx = newState.findIndex((item) => item.uid === messageUID);
                                    if (idx > -1) {
                                        const existingPartIdx = newState[idx].parts.findIndex(
                                            (part) =>
                                                part.type === 'json' &&
                                                part.jsonBlocks?.some((b) => b.data_type === 'stream_thinking'),
                                        );
                                        if (existingPartIdx > -1) {
                                            const existingBlock = newState[idx].parts[existingPartIdx]
                                                .jsonBlocks![0] as StreamThinking;
                                            newState[idx].parts[existingPartIdx] = {
                                                ...newState[idx].parts[existingPartIdx],
                                                jsonBlocks: [
                                                    {
                                                        ...existingBlock,
                                                        content: [
                                                            ...existingBlock.content,
                                                            ...(block as StreamThinking).content,
                                                        ],
                                                    },
                                                ],
                                            };
                                        } else {
                                            newState[idx].parts.push({
                                                ...messagePart,
                                                jsonBlocks: [block],
                                            });
                                        }
                                    }
                                    continue;
                                }

                                const existingBlockIndex =
                                    block.data_type === 'next-source'
                                        ? -1
                                        : newState[index].parts.findIndex(
                                              (part) =>
                                                  part.type === 'json' &&
                                                  part.jsonBlocks?.some(
                                                      (existingBlock) =>
                                                          JSON.stringify(existingBlock) === JSON.stringify(block),
                                                  ),
                                          );

                                if (existingBlockIndex > -1) {
                                    continue;
                                }

                                const partWithBlock = {
                                    ...messagePart,
                                    jsonBlocks: [block],
                                };

                                if (block.data_type === 'next-block') {
                                    const newUID = uid();
                                    responseUIDRef.current = newUID;
                                    messageUID = newUID;

                                    newState.push({
                                        uid: messageUID,
                                        type: 'response',
                                        parts: [partWithBlock],
                                        parameters: {
                                            requestPath: currentPath,
                                        },
                                    });
                                } else {
                                    if (block.data_type === 'auth-required') {
                                        const data = block.data;

                                        data.forEach((authData, index) => {
                                            if (index === 0) {
                                                const idx = newState.findIndex((item) => item.uid === messageUID);
                                                if (idx > -1) {
                                                    newState[idx].parts.push(partWithBlock);
                                                }
                                            } else {
                                                const newUID = uid();
                                                responseUIDRef.current = newUID;
                                                messageUID = newUID;

                                                newState.push({
                                                    uid: messageUID,
                                                    type: 'response',
                                                    parts: [
                                                        {
                                                            ...messagePart,
                                                            jsonBlocks: [
                                                                {
                                                                    data_type: 'auth-required',
                                                                    data: [authData],
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                    parameters: {
                                                        requestPath: currentPath,
                                                    },
                                                });
                                            }
                                        });

                                        // row 1880: a report's own sign-in has no "proceed": without it the report cannot answer
                                        if (offersProceed(block)) {
                                            const newUID = uid();
                                            responseUIDRef.current = newUID;
                                            messageUID = newUID;

                                            newState.push({
                                                uid: messageUID,
                                                type: 'response',
                                                parts: [
                                                    {
                                                        ...messagePart,
                                                        jsonBlocks: [
                                                            {
                                                                data_type: 'proceed-button',
                                                            },
                                                        ],
                                                    },
                                                ],
                                                parameters: {
                                                    requestPath: currentPath,
                                                },
                                            });
                                        }
                                    } else {
                                        const idx = newState.findIndex((item) => item.uid === messageUID);
                                        if (idx > -1) {
                                            newState[idx].parts.push(partWithBlock);
                                        }
                                    }
                                }
                            }
                            continue;
                        }

                        newState[index].parts.push(messagePart);
                    }
                }

                return newState;
            });
        },
        [activeChat, currentPath],
    );

    const processRequest = useCallback(
        async (additionalParams?: { [key: string]: string }, customExcludedTools?: string[]) => {
            removeProceedButtons();
            setLoading(true);
            dispatch(setWaitingConcierge(true));
            const generation = requestGenerationRef.current;
            const superseded = () => generation !== requestGenerationRef.current;
            let messageUID = responseUIDRef.current;
            const currentState = store.getState();

            const freshPrependContext = currentState.concierge.prependContext as {
                text: string;
                showInChat: boolean;
            } | null;
            const backendMessage =
                !isLearningUpdateCommand(activeChatRequest) && freshPrependContext?.text
                    ? freshPrependContext.text + '   ' + activeChatRequest
                    : activeChatRequest;

            if (freshPrependContext?.text) {
                dispatch(clearPrependContext());
            }

            const freshSelectedDomains = currentState.concierge.selectedDomains as string[];
            const allFreshAvailableDomains = currentState.concierge.availableDomains as IDomain[];

            const freshAvailableDomains = allFreshAvailableDomains;

            // Row 2675: at off the report is not asked for its context and the question carries none
            const externalContext = await collectReportContext(reportContextCaptureOn(currentState), getContext);
            if (superseded()) {
                return;
            }

            const effectiveParams: { [key: string]: string } = {
                proceed: proceedMode ? '1' : '0',
                ...additionalParams,
            };

            if (externalContext) {
                effectiveParams.context = externalContext;
            }

            const result = new Set<string>(toolsForSelectedSources(freshSelectedDomains, freshAvailableDomains));

            const toolsToExclude = customExcludedTools ?? excludedTools;

            const currentSelectedTools = Array.from(result).filter((tool) => {
                const toolName = tool.includes('|') ? tool.split('|')[0] : tool;
                return !toolsToExclude.includes(toolName);
            });

            // Domain Editor preview (Marius, 2026-09-04): the session is pinned to ONE domain, and
            // that domain may be switched off, so its tool groups come from the session rather than
            // from the panel's domain list, which only ever carries enabled domains. domain_preview
            // names the domain to the proxy, which checks the session row before honouring it.
            const previewSession = currentState.concierge.testSession as ITestSession | null;
            const domainPreview = previewSession?.scope_kind === 'domain' ? previewSession : null;
            const toolsForTurn = domainPreview ? (domainPreview.tools ?? []) : currentSelectedTools;
            if (domainPreview) {
                effectiveParams.domain_preview = String(domainPreview.concierge_domain_id);
            }
            // The turn names its source from the selection, never from its tool list: Report Data, or All Sources,
            // which posts the same list as an explicit Tiles pick where search is the only source.
            const sourceMarker = sourceMarkerForTurn(
                freshSelectedDomains,
                freshAvailableDomains,
                effectiveParams[REPORT_DATA_SOURCE_PARAM],
                !!domainPreview,
            );
            if (sourceMarker) {
                effectiveParams[REPORT_DATA_SOURCE_PARAM] = sourceMarker;
            }

            currentController?.abort();
            currentController = new AbortController();
            const signal = currentController.signal;

            const responseCancelledByUser = () => {
                if (superseded()) {
                    return;
                }
                setDialog((prevState) => {
                    const newState = [...prevState];
                    const index = newState.findIndex((item) => item.uid == messageUID);

                    if (index > 0) {
                        newState[index].parts.push({
                            type: 'html',
                            message: `<div class="stopped-request-message"><b>${t('message.stopped_by_user')}</b></div>`,
                            source: 'frontend',
                        });

                        const requestIndex = index - 1;
                        if (requestIndex >= 0 && newState[requestIndex].type === 'request') {
                            if (!newState[requestIndex].parameters) {
                                newState[requestIndex].parameters = {};
                            }
                            newState[requestIndex].parameters.cancelled = 'Y';
                        }
                    } else {
                        newState.push({
                            uid: uid(),
                            type: 'response',
                            parts: [
                                {
                                    type: 'html',
                                    message: `<div class="stopped-request-message"><b>${t('message.stopped_by_user')}</b></div>`,
                                    source: 'frontend',
                                },
                            ],
                            parameters: {
                                cancelled: 'Y',
                            },
                        });
                    }

                    return newState;
                });
            };

            const activeChatMessageUid: string = uuidv4();

            const selectedDomainIds = domainIdsForAdditionalData(freshSelectedDomains, freshAvailableDomains);

            // The additional-data poll usually resolves only after the main turn finishes, but the
            // client may still be draining the last stream chunks at that moment — appending then
            // would put the sections above the tail of the answer. Hold the block until the main
            // request settles.
            let mainRequestSettled = false;
            let pendingAdditionalDataBlock: AdditionalData | null = null;

            const appendAdditionalDataBlock = (block: AdditionalData) => {
                setDialog((prevState) => {
                    const newState = [...prevState];
                    const index = newState.findIndex((item) => item.uid == messageUID);

                    if (index > -1) {
                        newState[index].parts.push({
                            type: 'json',
                            message: '',
                            jsonBlocks: [block],
                            source: 'frontend',
                        });
                    }

                    return newState;
                });
            };

            conciergeAPI
                .getAdditionalRequestData(
                    {
                        q: backendMessage,
                        conversation_ts: activeChat,
                        message_uid: activeChatMessageUid,
                        ...(selectedDomainIds.length ? { domains: selectedDomainIds } : {}),
                        // The question-intent design (2026-09-21, cut 7): the panel's tools ride as listed; whether the
                        // documentation leg runs is the reader's kind, read by the back end (R16, the spelling check, is
                        // deleted); the explicit documentation domain selection still rides as `domains`.
                        tools: currentSelectedTools,
                        // Report Data (§6.4) states its scope on both carriers; the back end reads the marker, not the tool list.
                        ...(effectiveParams[REPORT_DATA_SOURCE_PARAM]
                            ? { [REPORT_DATA_SOURCE_PARAM]: effectiveParams[REPORT_DATA_SOURCE_PARAM] }
                            : {}),
                    },
                    signal,
                )
                .then((response) => {
                    const row = response?.data;
                    if (!row || Array.isArray(row)) {
                        return;
                    }

                    const block = additionalDataBlock(row, currentElementId);
                    if (!block) {
                        return;
                    }

                    if (mainRequestSettled) {
                        appendAdditionalDataBlock(block);
                    } else {
                        pendingAdditionalDataBlock = block;
                    }
                })
                .catch(() => {
                    // Best effort: aborts, timeouts, and empty payloads all degrade to today's answer-only view.
                });

            conciergeAPI
                .getAnswer(
                    backendMessage,
                    activeChat,
                    activeChatMessageUid,
                    useStream,
                    onlySearch,
                    currentController,
                    toolsForTurn,
                    {
                        ...extraUrlParams,
                        ...effectiveParams,
                    },
                    customInstructions,
                    launchElementId ??
                        editorContextElementId ??
                        (currentElementId ? Number(currentElementId) : undefined),
                )
                .then(async (response) => {
                    const stream: ReadableStream = response.data;
                    const reader = stream.getReader();
                    const decoder = new TextDecoder();

                    let aborted = false;

                    const abortHandler = () => {
                        aborted = true;
                        reader.cancel();
                        responseCancelledByUser();
                    };
                    signal.addEventListener('abort', abortHandler);

                    try {
                        while (!aborted) {
                            const { done, value } = await reader.read();

                            if (value) {
                                const textChunk = decoder.decode(value, { stream: true });
                                if (textChunk) {
                                    responseToContent(textChunk);
                                    // The turn is complete once the backend emits this control block;
                                    // re-enable input now instead of waiting for the stream to close
                                    // (under Apache mod_php the connection stays open during the
                                    // post-response experience-path capture). Keep draining the stream.
                                    if (textChunk.includes('turn_complete')) {
                                        setLoading(false);
                                    }
                                }
                            }

                            if (done) {
                                const finalText = decoder.decode();
                                if (finalText) {
                                    responseToContent(finalText);
                                }
                                break;
                            }
                        }
                    } finally {
                        signal.removeEventListener('abort', abortHandler);
                        reader.releaseLock();
                        setLoading(false);
                    }
                })
                .catch(async (error) => {
                    console.error('Unexpected error during request:', error);
                    if (error.name === 'CanceledError') {
                        responseCancelledByUser();
                    } else {
                        let errorMessage = error.message || 'Unknown error';
                        const responseData = error.response?.data;

                        if (responseData) {
                            if (typeof responseData === 'string') {
                                errorMessage = responseData;
                            } else if (responseData instanceof ReadableStream) {
                                try {
                                    const reader = responseData.getReader();
                                    const decoder = new TextDecoder();
                                    let streamContent = '';
                                    while (true) {
                                        const { done, value } = await reader.read();
                                        if (done) break;
                                        streamContent += decoder.decode(value, { stream: true });
                                    }
                                    errorMessage =
                                        streamContent ||
                                        error.response?.statusText ||
                                        `Request failed with status ${error.response?.status}`;
                                } catch {
                                    errorMessage =
                                        error.response?.statusText ||
                                        `Request failed with status ${error.response?.status}`;
                                }
                            } else if (typeof responseData === 'object' && responseData.error) {
                                errorMessage = responseData.error;
                            } else if (typeof responseData === 'object' && responseData.message) {
                                errorMessage = responseData.message;
                            }
                        }

                        setDialog((prevState) => {
                            const newState = [...prevState];

                            const index = newState.findIndex((item) => item.uid == messageUID);
                            if (index > -1) {
                                // A refusal the backend named travels as a code the panel can
                                // explain in its own words; everything else keeps the raw body.
                                let reasonCode: string | undefined;
                                try {
                                    const parsed = JSON.parse(errorMessage);
                                    if (parsed && typeof parsed.reason_code === 'string') {
                                        reasonCode = parsed.reason_code;
                                    }
                                } catch {
                                    // Not JSON: an ordinary error body, left exactly as it was.
                                }
                                const jsonPart: ErrorBlock = {
                                    error: errorMessage,
                                    ...(reasonCode ? { reason_code: reasonCode } : {}),
                                };

                                newState[index].parts.push({
                                    type: 'json',
                                    message: JSON.stringify(jsonPart, null, 2),
                                    jsonBlocks: [jsonPart],
                                    source: 'backend',
                                });
                            }

                            return newState;
                        });
                    }
                })
                .finally(() => {
                    mainRequestSettled = true;
                    if (superseded()) {
                        return;
                    }
                    if (pendingAdditionalDataBlock) {
                        appendAdditionalDataBlock(pendingAdditionalDataBlock);
                        pendingAdditionalDataBlock = null;
                    }
                    whyMessageUidRef.current[messageUID] = activeChatMessageUid;
                    if (onlySearch) {
                        dispatch(setOnlySearch(false));
                    }
                    setLastInteraction(Date.now());
                    setLoading(false);
                });
        },
        [
            activeChatRequest,
            activeChat,
            useStream,
            onlySearch,
            extraUrlParams,
            proceedMode,
            excludedTools,
            removeProceedButtons,
            responseToContent,
            dispatch,
            store,
            t,
            getContext,
            customInstructions,
            launchElementId,
            editorContextElementId,
            currentElementId,
        ],
    );

    // Why each named report is here (Marius, 2026-09-09): once the answer has settled, one request
    // per response asks for the wording of the reports that carry codes; the reply fills the
    // tooltips, and a timeout or an error removes the icons so the reports read as today.
    useEffect(() => {
        if (loading) {
            return;
        }
        dialog.forEach((item) => {
            if (item.type !== 'response' || whyRequestedRef.current.has(item.uid)) {
                return;
            }
            const messageUid = whyMessageUidRef.current[item.uid];
            const reportIds = messageUid ? collectWhyReportIds(item.parts) : [];
            if (!reportIds.length) {
                return;
            }
            whyRequestedRef.current.add(item.uid);
            requestReportWhy(conciergeAPI.getReportWhy, {
                conversation_ts: activeChat,
                message_uid: messageUid,
                report_ids: reportIds,
                report_names: collectWhyReportNames(item.parts),
            }).then((reply) => {
                setDialog((prevState) =>
                    prevState.map((entry) =>
                        entry.uid === item.uid
                            ? {
                                  ...entry,
                                  parts: reply
                                      ? applyReportWhyTexts(entry.parts, reply.reports)
                                      : stripReportWhy(entry.parts),
                              }
                            : entry,
                    ),
                );
            });
        });
    }, [dialog, loading, activeChat]);

    const onNewRequest = useCallback(() => {
        const request = activeChatRequestRef.current;
        if (request > '') {
            const requestUid = uid();
            const responseUid = uid();

            requestUIDRef.current = requestUid;
            responseUIDRef.current = responseUid;

            setDialog((prevState) => {
                const newState = [...prevState];

                newState.push({
                    uid: requestUid,
                    type: 'request',
                    parts: [
                        {
                            type: 'html',
                            message: request,
                            source: 'frontend',
                        },
                    ],
                });

                newState.push({
                    uid: responseUid,
                    type: 'response',
                    parts: [],
                    parameters: {
                        requestPath: currentPath,
                        selectedDomains: selectedConciergeDomains,
                    },
                });

                return newState;
            });

            processRequest();
        }
    }, [currentPath, selectedConciergeDomains, processRequest]);

    useEffect(() => {
        onNewRequest();
    }, [activeChatRequest, activeChat]);

    const onRequestSubmit = useCallback(
        (request: string) => {
            if (request == '') return;

            if (activeChatRequestRef.current == request) {
                onNewRequest();
            }

            activeChatRequestRef.current = request;
            setActiveChatRequest(request);
        },
        [onNewRequest],
    );

    const clearActiveChatRequest = useCallback(() => {
        activeChatRequestRef.current = '';
        setActiveChatRequest('');
    }, []);

    const startNewChat = useCallback(() => {
        setActiveChat(0);
        clearActiveChatRequest();
        setDialog([]);
        setExcludedTools([]);
        setLastInteraction(Date.now());
        setProceedMode(false);
        if (isElementViewer) {
            const elementId = matches[matches.length - 1].params?.elementId as string;

            if (mappedDomains[elementId] && mappedDomains[elementId].length > 0) {
                dispatch(setSelectedDomains(mappedDomains[elementId]));
                dispatch(setDefaultSelectedDomains(mappedDomains[elementId]));
            } else {
                dispatch(setDefaultSelectedDomains(undefined));
            }
        } else {
            if (isAppPanel && selectedDomains !== undefined) {
                const currentAvailable = store.getState().concierge.availableDomains as IDomain[];
                dispatch(setSelectedDomains(canonicalizeDomainNames(selectedDomains, currentAvailable)));
            } else {
                dispatch(setSelectedDomains([]));
            }
            dispatch(setDefaultSelectedDomains(undefined));
        }
    }, [isElementViewer, isAppPanel, selectedDomains, mappedDomains, matches, dispatch, store]);

    // A stored chat reopened from Recent Chats: its turns replace the dialog and new questions continue
    // under its conversation_ts. An answer still streaming is cut off first.
    const openChat = useCallback(
        (conversationTs: string, restored: IDialogItem[], whyMessageUids: Record<string, string>) => {
            requestGenerationRef.current += 1;
            currentController?.abort();
            setLoading(false);
            clearActiveChatRequest();
            setExcludedTools([]);
            setProceedMode(false);
            whyMessageUidRef.current = { ...whyMessageUids };
            whyRequestedRef.current = new Set();
            setDialog(restored);
            setActiveChat(conversationTs);
            setLastInteraction(Date.now());
        },
        [clearActiveChatRequest],
    );

    const onAbort = useCallback(() => {
        setLoading(false);
        currentController?.abort();
    }, []);

    const silentAbort = useCallback(() => {
        setLoading(false);
    }, []);

    const onResendRequest = useCallback(
        (excludedToolsFromAuth: string[]) => {
            setProceedMode(true);
            setExcludedTools(excludedToolsFromAuth);
            const newResponseUid = uid();
            responseUIDRef.current = newResponseUid;

            setDialog((prevState) => {
                const newState = [...prevState];

                newState.push({
                    uid: newResponseUid,
                    type: 'response',
                    parts: [],
                    parameters: {
                        requestPath: currentPath,
                        selectedDomains: selectedConciergeDomains,
                    },
                });

                return newState;
            });

            setTimeout(() => {
                processRequest({ proceed: '1' }, excludedToolsFromAuth);
            }, 50);
        },
        [currentPath, selectedConciergeDomains, processRequest],
    );

    const onRetryError = useCallback(() => {
        const newResponseUid = uid();
        responseUIDRef.current = newResponseUid;

        setDialog((prevState) => {
            let lastRequestIdx = -1;
            for (let i = prevState.length - 1; i >= 0; i--) {
                if (prevState[i].type === 'request') {
                    lastRequestIdx = i;
                    break;
                }
            }
            const newState = prevState.slice(0, lastRequestIdx + 1);
            newState.push({
                uid: newResponseUid,
                type: 'response',
                parts: [],
                parameters: {
                    requestPath: currentPath,
                    selectedDomains: selectedConciergeDomains,
                },
            });
            return newState;
        });

        setTimeout(() => {
            processRequest();
        }, 50);
    }, [currentPath, selectedConciergeDomains, processRequest]);

    // Handle visibility and auto-start new chat
    useEffect(() => {
        if (visible) {
            const numberOfMinutesBeforeStartNewChat = startNewChatAfter
                ? startNewChatAfter
                : conciergeSettings.number_of_minutes_before_start_new_chat;

            if (numberOfMinutesBeforeStartNewChat > 0 && !testSession) {
                const beforeStart = numberOfMinutesBeforeStartNewChat * 60 * 1000;
                const now = Date.now();
                const expiredOn = lastInteraction + beforeStart;

                if (now > expiredOn) {
                    startNewChat();
                }
            }
            let isSetDefaultSelectedDomains = false;
            if (isElementViewer && dialog.length === 0) {
                const elementId = matches[matches.length - 1].params?.elementId as string;

                if (mappedDomains[elementId] && mappedDomains[elementId].length > 0) {
                    dispatch(setSelectedDomains(mappedDomains[elementId]));
                    dispatch(setDefaultSelectedDomains(mappedDomains[elementId]));
                } else {
                    dispatch(setDefaultSelectedDomains(undefined));
                }
                isSetDefaultSelectedDomains = true;
            }
            if (!isSetDefaultSelectedDomains) {
                dispatch(setDefaultSelectedDomains(undefined));
            }
        }
    }, [visible, isElementViewer, isAppPanel, testSession]);

    // Apply the app-panel selectedDomains prop, re-applying whenever the prop
    // itself changes. The shared-components flow reuses a singleton Redux store,
    // so a stale selection survives repeated renders; keying on the prop value
    // (not on the Redux selection being empty) lets a new prop override it.
    const selectedDomainsKey = selectedDomains ? JSON.stringify(selectedDomains) : undefined;
    useEffect(() => {
        if (
            visible &&
            isAppPanel &&
            selectedDomains !== undefined &&
            dialog.length === 0 &&
            reduxAvailableDomains.length > 0
        ) {
            dispatch(setSelectedDomains(canonicalizeDomainNames(selectedDomains, reduxAvailableDomains)));
            dispatch(setDefaultSelectedDomains(undefined));
        }
    }, [visible, isAppPanel, selectedDomainsKey, reduxAvailableDomains]);

    // Report Data is a standing entry on every page (Concierge_Panel.md §6.4); over a report viewer the
    // state and info text follow that report, so the entry is re-read when the element changes.
    useEffect(() => {
        let stale = false;

        conciergeAPI
            .getReportDataSource(currentElementId)
            .then((source) => {
                if (stale) {
                    return;
                }
                if (!source) {
                    setReportDataDomain(null);
                    return;
                }

                setReportDataDomain({
                    concierge_domain_id: REPORT_DATA_SOURCE_NAME,
                    name: REPORT_DATA_SOURCE_NAME,
                    title: source.title,
                    icon_ind: source.icon_ind,
                    icon_name: source.icon_name,
                    icon_data: source.icon_data,
                    description: t(reportDataInfoKey(source.state)),
                    reportDataState: source.state,
                });
            })
            .catch((error) => {
                console.error('[useConciergeChat] Failed to fetch report data source:', error);
                // Better no entry than the previous page's entry under this page's context.
                if (!stale) {
                    setReportDataDomain(null);
                }
            });

        return () => {
            stale = true;
        };
    }, [currentElementId]);

    // Handle domains data
    useEffect(() => {
        if (!domainsData || !Array.isArray(domainsData)) {
            return;
        }

        const resultAvailableDomains = domainsData.filter((domain) => {
            if (domain.name === 'search' && includeContentSearch !== undefined) {
                return includeContentSearch;
            }

            if (domain.name === 'glossary' && includeGlossaryTerms !== undefined) {
                return includeGlossaryTerms;
            }

            if (domain.name === 'faq' && includeCustomFaq !== undefined) {
                return includeCustomFaq;
            }

            if (domain.name === 'docs' && includeHelpDocs !== undefined) {
                return includeHelpDocs;
            }

            if (availableDomains) {
                const target = domain.name.trim().toLowerCase();
                return availableDomains.some((n) => n.trim().toLowerCase() === target);
            }

            return true;
        });

        const domainsWithReportData = reportDataDomain
            ? [...resultAvailableDomains, reportDataDomain]
            : resultAvailableDomains;

        dispatch(setAvailableDomains(domainsWithReportData));
    }, [
        domainsData,
        includeContentSearch,
        includeGlossaryTerms,
        includeCustomFaq,
        includeHelpDocs,
        availableDomains,
        reportDataDomain,
    ]);

    // Handle tools data
    useEffect(() => {
        if (toolsData && Array.isArray(toolsData)) {
            dispatch(setAvailableTools(toolsData));
        }
    }, [toolsData]);

    // Clean up only-status messages
    useEffect(() => {
        if (!loading && dialog.length > 0) {
            const lastMessage = dialog[dialog.length - 1];

            if (lastMessage.parts.length === 0) {
                return;
            }

            let onlyStatuses = true;

            lastMessage.parts.forEach((part) => {
                if (part.type === 'markdown' || part.type === 'html' || part.type === 'invalid-request') {
                    onlyStatuses = false;
                } else if (part.type === 'json') {
                    part.jsonBlocks?.forEach((block) => {
                        if (
                            block.data_type !== 'status-badge' &&
                            block.data_type !== 'next-block' &&
                            block.data_type !== 'progress'
                        ) {
                            onlyStatuses = false;
                        }
                    });
                }
            });

            if (onlyStatuses) {
                const newDialog = dialog.slice(0, -1);
                setDialog(newDialog);
            }
        }
    }, [dialog, loading]);

    // Handle pending new chat
    useEffect(() => {
        if (pendingNewChat && domainsData && toolsData) {
            setActiveChatRequest(pendingNewChat);
            setPendingNewChat(null);
        }
    }, [pendingNewChat, domainsData, toolsData]);

    const startNewResponseBlock = useCallback(() => {
        const newUID = uid();
        responseUIDRef.current = newUID;
        setDialog((prev) => [
            ...prev,
            {
                uid: newUID,
                type: 'response',
                parts: [],
                parameters: {
                    requestPath: currentPath,
                },
            },
        ]);
    }, [currentPath]);

    return {
        dialog,
        setDialog,
        loading,
        activeChat,
        setActiveChat,
        activeChatRequest,
        onRequestSubmit,
        startNewChat,
        onAbort,
        silentAbort,
        isElementViewer,
        mappedDomains,
        domainsData,
        toolsData,
        onResendRequest,
        onRetryError,
        setLastInteraction,
        clearActiveChatRequest,
        startNewResponseBlock,
        openChat,
    };
}

export interface NewChatHandler {
    handleNewChat: (newChat: string) => void;
    clearPendingNewChat: () => void;
}

export function useNewChatHandler(
    domainsData: IDomain[] | undefined,
    toolsData: ITool[] | undefined,
    setDialog: React.Dispatch<React.SetStateAction<IDialogItem[]>>,
    setActiveChat: React.Dispatch<React.SetStateAction<ChatId>>,
    onRequestSubmit: (request: string) => void,
    dispatch: any,
): NewChatHandler {
    const [pendingNewChat, setPendingNewChat] = useState<string | null>(null);

    useEffect(() => {
        if (pendingNewChat && domainsData && toolsData) {
            onRequestSubmit(pendingNewChat);
            setPendingNewChat(null);
        }
    }, [pendingNewChat, domainsData, toolsData, onRequestSubmit]);

    const handleNewChat = useCallback(
        (newChat: string) => {
            setDialog([]);
            setActiveChat(Date.now());
            dispatch(setSelectedDomains([]));

            if (domainsData && toolsData) {
                onRequestSubmit(newChat);
            } else {
                setPendingNewChat(newChat);
            }
        },
        [domainsData, toolsData, setDialog, setActiveChat, onRequestSubmit, dispatch],
    );

    const clearPendingNewChat = useCallback(() => {
        setPendingNewChat(null);
    }, []);

    return {
        handleNewChat,
        clearPendingNewChat,
    };
}
